import { Eye, MousePointer, Filter, Search } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import type { BrowseBehaviorResult } from '../../../../api/widgetTelemetry';
import StatCard from '../shared/StatCard';

interface BrowseBehaviorProps {
  data: BrowseBehaviorResult | null;
  loading: boolean;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

export default function BrowseBehavior({ data, loading }: BrowseBehaviorProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const topTypesChart = data.topWorkshopTypes.slice(0, 8).map((t) => ({
    type: t.workshopType.length > 24 ? `${t.workshopType.slice(0, 24)}…` : t.workshopType,
    impressions: t.impressions,
    clicks: t.clicks,
  }));

  const hasImpressions = data.impressions.totalImpressions > 0;

  return (
    <div className="space-y-6">
      {/* Top-line KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Card Impressions"
          value={data.impressions.totalImpressions.toLocaleString()}
          icon={Eye}
          iconColor="text-water-600"
        />
        <StatCard
          label="Card Clicks"
          value={data.impressions.totalClicks.toLocaleString()}
          icon={MousePointer}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Click-through Rate"
          value={pct(data.impressions.clickThroughRate)}
          icon={MousePointer}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Filter → Select"
          value={pct(data.filterEngagement.filterToSelectRate)}
          icon={Filter}
          iconColor="text-amber-600"
        />
      </div>

      {/* Secondary stat row */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Sessions Applying Filters"
          value={data.filterEngagement.sessionsApplyingFilters.toLocaleString()}
          icon={Filter}
          iconColor="text-water-600"
        />
        <StatCard
          label="Sessions Re-searching"
          value={data.searchBehavior.sessionsWithLocationChange.toLocaleString()}
          icon={Search}
          iconColor="text-amber-600"
        />
        <StatCard
          label="Sessions Using Load More"
          value={data.engagement.sessionsUsingLoadMore.toLocaleString()}
          icon={Eye}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Sessions Switching View"
          value={data.engagement.sessionsSwitchingView.toLocaleString()}
          icon={Eye}
          iconColor="text-stone-500"
        />
      </div>

      {/* Top workshop types chart */}
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
        <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">
          Top Workshop Types — impressions vs clicks
        </h3>
        {!hasImpressions ? (
          <p className="text-xs text-stone-400 text-center py-8">
            No impression data yet. Card impressions started firing in Phase 0
            — if the widget build has been deployed but this panel is empty,
            double-check that the <code>workshop_card_impression</code> custom
            dimensions are registered on GA4 property 530668124.
          </p>
        ) : topTypesChart.length === 0 ? (
          <p className="text-xs text-stone-400 text-center py-8">No workshop-type breakdown available</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topTypesChart} layout="vertical" margin={{ left: 120, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} width={120} />
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts v3 Tooltip formatter types are overly strict */}
              <Tooltip formatter={((value: number) => value.toLocaleString()) as any} />
              <Bar dataKey="impressions" name="Impressions" fill="#4A6741" radius={[0, 4, 4, 0]} />
              <Bar dataKey="clicks" name="Clicks" fill="#F59E0B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top workshop types table */}
      {data.topWorkshopTypes.length > 0 && (
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">Workshop Type Detail</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-[#1e2520]">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Workshop Type</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Impressions</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Clicks</th>
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">CTR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {data.topWorkshopTypes.map((row) => (
                <tr key={row.workshopType} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                  <td className="px-4 py-2 text-bark-900 dark:text-stone-200">{row.workshopType}</td>
                  <td className="px-4 py-2 text-right">{row.impressions.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{row.clicks.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{pct(row.ctr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filter + search detail */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-3">Filter Engagement</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Total applications</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.filterEngagement.totalFilterApplications.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Total clears</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.filterEngagement.totalFilterClears.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Sessions applying filters</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.filterEngagement.sessionsApplyingFilters.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Filter → Select rate (upper bound)</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {pct(data.filterEngagement.filterToSelectRate)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-3">Search Behavior</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Sessions changing location</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.searchBehavior.sessionsWithLocationChange.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Total location changes</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.searchBehavior.totalLocationChanges.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 dark:text-stone-400">Retry searches</dt>
              <dd className="font-medium text-bark-900 dark:text-stone-200">
                {data.searchBehavior.retrySearches.toLocaleString()}
              </dd>
            </div>
          </dl>
          <p className="text-xs text-stone-400 mt-3">
            Retry searches = location_changed events beyond the first per session. High values
            suggest users can&apos;t find what they&apos;re looking for nearby.
          </p>
        </div>
      </div>
    </div>
  );
}
